import { useState } from "react";
import { Loader2, Mail } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Checkbox } from "../ui/checkbox";
import { fmtPrice } from "../../data/mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const OrderBump = ({ checked, onChange, delta, bundleTotal }) => (
  <label
    className={`flex cursor-pointer items-start gap-3 rounded-2xl border p-4 transition-colors ${
      checked ? "border-[#2f7fff] bg-[#2f7fff]/10" : "border-white/15 bg-white/5"
    }`}
    data-testid="order-bump-option"
  >
    <Checkbox
      checked={checked}
      onCheckedChange={(v) => onChange(!!v)}
      className="mt-0.5 border-white/30 data-[state=checked]:bg-[#2f7fff] data-[state=checked]:border-[#2f7fff]"
      data-testid="order-bump-checkbox"
    />
    <span className="text-sm">
      <span className="font-semibold text-white">⚡ Add the full Card Guide for just A${fmtPrice(delta)} more</span>
      <span className="mt-1 block text-zinc-400">
        Get the complete step-by-step system + supplier access. Both PDFs, A${fmtPrice(bundleTotal)} total.
      </span>
    </span>
  </label>
);

export const CheckoutDialog = ({ product, pkgData, onClose }) => {
  const [email, setEmail] = useState("");
  const [bump, setBump] = useState(false);
  const [loading, setLoading] = useState(false);

  const productAmount = pkgData?.[product.id]?.amount ?? product.price;
  const bundleAmount = pkgData?.bundle?.amount ?? 49;
  const withBump = product.id === "supplier" && bump;
  const total = withBump ? bundleAmount : productAmount;

  const startCheckout = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/checkout/session`, {
        package_id: withBump ? "bundle" : product.id,
        origin_url: window.location.origin,
        email,
      });
      window.location.href = data.url;
    } catch {
      toast.error("Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && !loading && onClose()}>
      <DialogContent className="border-white/10 bg-[#0b0d10] text-white sm:max-w-md" style={{ backgroundColor: "#0b0d10" }} data-testid="email-checkout-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-tight">{product.name}</DialogTitle>
          <DialogDescription className="text-zinc-400">
            Enter your email — your PDF is sent to your inbox as soon as payment goes through.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={startCheckout} className="space-y-4">
          {product.id === "supplier" && (
            <OrderBump
              checked={bump}
              onChange={setBump}
              delta={Math.max(bundleAmount - productAmount, 0)}
              bundleTotal={bundleAmount}
            />
          )}
          <div className="relative">
            <Mail className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              className="w-full rounded-full border border-white/15 bg-white/5 py-3 pl-11 pr-4 text-sm text-white placeholder:text-zinc-500 outline-none focus:border-[#2f7fff]"
              data-testid="checkout-email-input"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-[#2f7fff] hover:bg-[#4b91ff] disabled:opacity-60 py-3.5 font-semibold text-white transition-colors"
            data-testid="checkout-continue-button"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Redirecting to secure checkout...
              </>
            ) : (
              <>Continue to payment — {product.currency}{fmtPrice(total)} AUD</>
            )}
          </button>
          <p className="text-center text-xs text-zinc-500">Secure payment powered by Stripe</p>
        </form>
      </DialogContent>
    </Dialog>
  );
};
